import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import type { WhiteListItem as WhiteListItemType } from 'store/reducers/settings'

import { formatCurrency } from 'utils/formatters'
import { truncateWord } from 'utils/truncate-address'

import { NetworkLabel } from 'components/ui/network-label'
import { CopyButtonWithSuccess } from 'components/ui/copy-button-with-success'

import cn from 'classnames'

import { ReactSVG } from 'react-svg'

import TrashIcon from './icons/trash.svg'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'
import Tooltip from 'components/global/tooltip'
import useWindowWidth from 'hooks/use-window-width'

export const WhiteListItem = ({
  item,
  refetch,
  isDeleteActive = true,
  isInSelect = false,
  isTruncate = false,
  setDeleteAddressPopupVisible,
  className,
}: {
  item: WhiteListItemType
  setDeleteAddressPopupVisible: Dispatch<SetStateAction<WhiteListItemType>>
  refetch?: () => void
  isDeleteActive?: boolean
  isInSelect?: boolean
  isTruncate?: boolean
  className?: string
}) => {
  const { t } = useTranslation()
  const width = useWindowWidth()

  const [isNetwork, setIsNetwork] = useState(false)

  const [isEllipsis, setIsEllipsis] = useState(false)
  const textRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const checkEllipsis = () => {
      if (textRef.current) {
        const el = textRef.current
        const hasEllipsis = el.scrollWidth > el.clientWidth
        setIsEllipsis(hasEllipsis)
      }
    }

    const timeout = setTimeout(checkEllipsis, 0)

    return () => clearTimeout(timeout)
  }, [item.address_name, width])

  useEffect(() => {
    setIsNetwork(item.currency?.network.toLowerCase() !== item.currency?.code.toLowerCase())
  }, [])

  return (
    <div
      className={cn(styles.wrapper, { [styles.inSelect]: isInSelect }, { [styles.withNetwork]: isNetwork }, className)}
    >
      <div className={cn(styles.wallet, styles.wrapper_cell)}>
        <div className={styles.wallet_info}>
          <img className={styles.wallet_img} src={item?.currency?.logo_url} alt={`${item.currency?.code} logo`} />
          <div className={styles.wallet__mainInfo}>{formatCurrency(item.currency, true)}</div>
        </div>
        <NetworkLabel currency={item.currency} className={styles.wallet__network} />
      </div>

      {(!isInSelect || (isInSelect && item.address_name)) &&
        <div className={cn(styles.wrapper_cell, styles.wrapper_cell_desktop, styles['has-tooltip'])}>
          <div className={cn(styles.wrapper__text)}>
            <span>{item.address_name}</span>
          </div>

          {item?.address_name?.length >= 20 && !isInSelect &&
            <Tooltip className={styles.tooltip}>
              <span>{item.address_name}</span>
            </Tooltip>
          }
        </div>
      }

      <div className={cn(styles.wrapper_full, styles.wrapper_cell, styles.wrapper_cell_desktop)}>
        <div className={styles.address}>
          <p>
            {isTruncate
              ? isInSelect
                ? window.innerWidth > 768
                  ? item.address
                  : truncateWord(item.address, 6, 6)
                : truncateWord(item.address, 8, 8)
              : item.address}
          </p>
          <CopyButtonWithSuccess value={item.address} />
        </div>

        {item.extraId && (
          <span className={styles.extraId}>
            <p>
              {isTruncate
                ? window.innerWidth > 768
                  ? truncateWord(item.extraId, 6, 6)
                  : truncateWord(item.extraId, 3, 3)
                : item.extraId}
            </p>
            <CopyButtonWithSuccess value={item.extraId} />
          </span>
        )}
      </div>

      {isInSelect ? (
        <>
          {item.address_name && (
            <div className={cn(styles.wrapper_cell_mobile, styles.wrapper_cell_mobile_addressName, styles.mobileRow)}>
              <div className={cn(styles.wrapper__text)}>
                <span>{item.address_name}</span>
              </div>
            </div>
          )}

          <div className={cn(styles.wrapper_cell_mobile, styles.wrapper_cell_mobile_address, styles.mobileRow)}>
            <div className={cn(styles.textWithCopy, styles.wrapper__text)}>
              <div className={cn(styles.wrapper__text)}>
                <span>{item.address}</span>
              </div>
              <CopyButtonWithSuccess value={item.address} />
            </div>
          </div>

          {item.extraId && (
            <div className={cn(styles.wrapper_cell_mobile, styles.wrapper_cell_mobile_tag, styles.mobileRow)}>
              <div className={cn(styles.textWithCopy, styles.wrapper__text)}>
                <div className={cn(styles.wrapper__text)}>
                  <span>{item.extraId}</span>
                </div>
                <CopyButtonWithSuccess value={item.extraId} />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.info}>
          {item.address_name && (
            <div className={cn(styles.wrapper_cell_mobile, styles.wrapper_cell_mobile_addressName, styles.mobileRow,
              styles['has-tooltip']
            )}>
              <span>{t('name')}</span>
              <div className={cn(styles.wrapper__text)}>
                <span ref={textRef}>{item.address_name}</span>
              </div>
              {isEllipsis &&
                <Tooltip className={styles.tooltip}>
                  <span>{item.address_name}</span>
                </Tooltip>
              }
            </div>
          )}

          <div className={cn(styles.wrapper_cell_mobile, styles.wrapper_cell_mobile_address, styles.mobileRow)}>
            <span>{t('address')}</span>
            <div className={styles.textWithCopy}>
              <p>{isTruncate ? truncateWord(item.address, 8, 8) : item.address}</p>
              <CopyButtonWithSuccess value={item.address} />
            </div>
          </div>

          {item.extraId && (
            <div className={cn(styles.wrapper_cell_mobile, styles.wrapper_cell_mobile_tag, styles.mobileRow)}>
              <span>{t('tag')}</span>
              <div className={styles.textWithCopy}>
                <p>{isTruncate ? truncateWord(item.extraId, 4, 4) : item.extraId}</p>
                <CopyButtonWithSuccess value={item.extraId} />
              </div>
            </div>
          )}
        </div>
      )}

      {isDeleteActive ? (
        <div className={styles.wrapper_cell} onClick={() => setDeleteAddressPopupVisible(item)}>
          <ReactSVG className={styles.trash} src={TrashIcon} />
        </div>
      ) : (
        <></>
      )}
    </div>
  )
}
